// src/App.js
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

import NavigationBar from './components/NavigationBar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import ProtectedRoute from './components/ProtectedRoute';
import BackHandler from './components/BackHandler';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import { initErrorTracking } from './utils/errorTracking';
import HomePage from './pages/HomePage';
import ServicePage from './pages/ServicePage';
import CombinedLoginPage from './pages/CombinedLoginPage';
import SignUpPage from './pages/SignUpPage';
import CustomerSignUpPage from './pages/CustomerSignUpPage';
import ProviderSignUpPage from './pages/ProviderSignUpPage';
import CustomerProfile from './pages/CustomerProfile';
import ProviderProfile from './pages/ProviderProfile';
import CustomerProfileForm from './pages/CustomerProfileForm';
import ProviderProfileForm from './pages/ProviderProfileForm';
import EditCarrierProfile from './pages/EditCarrierProfile';
import EditCustomerProfile from './pages/EditCustomerProfile';
import CustomerDashboard from './pages/CustomerDashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import AdminPanel from './pages/AdminPanel';
import CarrierProfile from './pages/CarrierProfile';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PaymentSuccess from './pages/PaymentSuccess';
import CarrierAgreement from './pages/CarrierAgreement';
import NotFoundPage from './pages/NotFoundPage';
import AreasIndexPage from './pages/AreasIndexPage';
import AreaPage from './pages/AreaPage';
import JobPage from './pages/JobPage';
import GuidesIndexPage from './pages/GuidesIndexPage';
import GuidePage from './pages/GuidePage';
import HowItWorksPage from './pages/HowItWorksPage';
import TownLandingPage from './pages/TownLandingPage';
import { TOWN_LANDINGS } from './data/townLandings';

import './styles/global.css';

// Register global error handlers once at module load time (after all imports)
initErrorTracking();

// /dashboard is a convenience URL: send the (verified) user to their own dashboard.
// ProtectedRoute handles logged-out -> /login and unverified -> /verify-email.
const DashboardRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={user.type === 'provider' ? '/provider-dashboard' : '/customer-dashboard'} replace />;
};

const App = () => (
  <AuthProvider>
    <div className="app-container">
      <Router>
        <ScrollToTop />
        <NavigationBar />
        <div className="main-content">
          <ErrorBoundary>
          <Routes>
            {/* Public routes */}
            <Route path="/"               element={<HomePage />} />
            <Route path="/services"       element={<ServicePage />} />
            <Route path="/how-it-works"   element={<HowItWorksPage />} />
            {TOWN_LANDINGS.map((town) => (
              <Route key={town.path} path={town.path} element={<TownLandingPage />} />
            ))}
            <Route path="/areas"          element={<AreasIndexPage />} />
            <Route path="/areas/:slug"    element={<AreaPage />} />
            <Route path="/swansea/:slug"  element={<JobPage />} />
            <Route path="/guides"         element={<GuidesIndexPage />} />
            <Route path="/guides/:slug"   element={<GuidePage />} />
            <Route path="/login"          element={<CombinedLoginPage />} />
            <Route path="/signup"         element={<SignUpPage />} />
            <Route path="/customer-signup" element={<CustomerSignUpPage />} />
            <Route path="/provider-signup" element={<ProviderSignUpPage />} />
            <Route path="/admin"          element={<AdminPanel />} />
            <Route path="/carrier/:carrierId" element={<CarrierProfile />} />
            <Route path="/terms"          element={<TermsOfService />} />
            <Route path="/privacy"        element={<PrivacyPolicy />} />
            <Route path="/verify-email"   element={<VerifyEmailPage />} />
            <Route path="/carrier-agreement" element={<CarrierAgreement />} />

            {/* Protected — any authenticated user */}
            <Route path="/customer-profile" element={
              <ProtectedRoute requiredType="customer"><CustomerProfile /></ProtectedRoute>
            } />
            <Route path="/provider-profile" element={
              <ProtectedRoute requiredType="provider"><ProviderProfile /></ProtectedRoute>
            } />
            <Route path="/customer-profile/create" element={
              <ProtectedRoute requiredType="customer"><CustomerProfileForm /></ProtectedRoute>
            } />
            <Route path="/provider-profile/create" element={
              <ProtectedRoute requiredType="provider"><ProviderProfileForm /></ProtectedRoute>
            } />
            <Route path="/provider-profile/edit" element={
              <ProtectedRoute requiredType="provider"><EditCarrierProfile /></ProtectedRoute>
            } />
            <Route path="/customer-profile/edit" element={
              <ProtectedRoute requiredType="customer"><EditCustomerProfile /></ProtectedRoute>
            } />
            <Route path="/customer-dashboard" element={
              <ProtectedRoute requiredType="customer"><CustomerDashboard /></ProtectedRoute>
            } />
            <Route path="/payment-success" element={
              <ProtectedRoute requiredType="customer"><PaymentSuccess /></ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute><DashboardRedirect /></ProtectedRoute>
            } />
            <Route path="/provider-dashboard" element={
              <ProtectedRoute requiredType="provider"><ProviderDashboard /></ProtectedRoute>
            } />

            {/* Unknown paths only. Auth email continue URLs (/verify-email, /login) are registered above. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </ErrorBoundary>
        </div>
        <Footer />
        <CookieConsent />
        <BackHandler />
      </Router>
    </div>
  </AuthProvider>
);

export default App;
